import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ArrowLeft } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Sesión iniciada");
      const from = location.state?.from?.pathname;
      if (u.payment_status !== "paid") navigate("/pago");
      else if (!u.has_profile) navigate("/crear-perfil");
      else navigate(from || "/panel");
    } catch (err) {
      const status = err?.response?.status;
      let msg;
      if (status === 401) {
        // Wrong credentials / user not found — SAME message for both (no email enumeration)
        msg = "Correo o contraseña incorrectos.";
      } else if (status >= 500 && status < 600) {
        // Backend / Supabase / DB temporarily unavailable
        msg = "El servidor está temporalmente no disponible. Inténtalo de nuevo en unos minutos.";
      } else if (!err?.response) {
        // No HTTP response received → network error / timeout / offline
        msg = "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.";
      } else {
        // Any other client-side validation error from backend (e.g. malformed body)
        msg = err?.response?.data?.detail || "No pudimos iniciar tu sesión. Inténtalo de nuevo.";
        if (Array.isArray(msg)) msg = "No pudimos iniciar tu sesión. Inténtalo de nuevo.";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--mep-black)] text-white mep-grain flex flex-col">
      <header className="px-5 md:px-10 py-5">
        <Link to="/" data-testid="login-back-link" className="flex items-center gap-2 text-sm text-[var(--mep-text-muted)] hover:text-white">
          <ArrowLeft size={16} /> Inicio
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md">
          <div className="mep-pill-gold mb-4">Acceso</div>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter mb-2">Iniciar sesión</h1>
          <p className="text-[var(--mep-text-muted)] mb-8">Bienvenido de nuevo.</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Correo</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)] transition"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Contraseña</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)] transition"
              />
            </div>
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="btn-pink w-full py-4 font-semibold"
            >
              {loading ? "Entrando..." : "Entrar"}
            </button>
            <div className="text-center">
              <Link
                to="/olvide-contrasena"
                data-testid="login-forgot-link"
                className="text-sm text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] transition"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
